import { useState, useEffect, useCallback, useRef } from 'react';
import { asyncDataDiagnosisService, JobStatus } from '../services/asyncDataDiagnosisService';

const PHASE_LABELS: Record<string, string> = {
    ingestion: 'Ingesting data',
    profiling: 'Profiling',
    reasoning: 'Analyzing issues',
    remediation: 'Generating remediation',
    report: 'Generating report',
    translating: 'Translating report',
    completed: 'Completed',
};

interface UseAsyncJobOptions {
    onJobComplete?: (status: JobStatus) => void;
    onJobUpdate?: (status: JobStatus) => void;
    pollingInterval?: number;
    autoStart?: boolean;
}

interface UseAsyncJobResult {
    jobStatus: JobStatus | null;
    isPolling: boolean;
    error: string | null;
    startPolling: (jobId: string) => void;
    stopPolling: () => void;
    clearError: () => void;
    reset: () => void;
    progress: number;
    currentPhase: string;
    isComplete: boolean;
    isSuccessful: boolean;
    isFailed: boolean;
    isPending: boolean;
    isRunning: boolean;
    historicalReportId: string | null;
}

/**
 * Hook for managing async data diagnosis jobs
 */
export const useAsyncJob = (options: UseAsyncJobOptions = {}): UseAsyncJobResult => {
    const {
        onJobComplete,
        onJobUpdate,
        pollingInterval = 5000, // 5 seconds - original interval
    } = options;

    const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
    const [isPolling, setIsPolling] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const currentJobIdRef = useRef<string | null>(null);

    const startPolling = useCallback(async (jobId: string) => {
        if (isPolling && currentJobIdRef.current === jobId) {
            return; // Already polling this job
        }

        // Stop any existing polling
        if (currentJobIdRef.current) {
            asyncDataDiagnosisService.stopPolling(currentJobIdRef.current);
        }

        currentJobIdRef.current = jobId;
        setIsPolling(true);
        setError(null);

        try {
            await asyncDataDiagnosisService.pollJobUntilComplete(
                jobId,
                (status) => {
                    setJobStatus(status);
                    onJobUpdate?.(status);
                },
                pollingInterval
            ).then((finalStatus) => {
                setIsPolling(false);
                onJobComplete?.(finalStatus);
            });
        } catch (err) {
            console.error('❌ [ASYNC_JOB] Polling failed:', err);
            setError(err instanceof Error ? err.message : 'Failed to poll job status');
            setIsPolling(false);
        }
    }, [isPolling, onJobComplete, onJobUpdate, pollingInterval]);

    const stopPolling = useCallback(() => {
        if (currentJobIdRef.current) {
            asyncDataDiagnosisService.stopPolling(currentJobIdRef.current);
            currentJobIdRef.current = null;
        }
        setIsPolling(false);
    }, []);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const reset = useCallback(() => {
        if (currentJobIdRef.current) {
            asyncDataDiagnosisService.stopPolling(currentJobIdRef.current);
            currentJobIdRef.current = null;
        }
        setJobStatus(null);
        setIsPolling(false);
        setError(null);
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (currentJobIdRef.current) {
                asyncDataDiagnosisService.stopPolling(currentJobIdRef.current);
            }
        };
    }, []);

    const isComplete = jobStatus ? ['SUCCESS', 'FAILED', 'CANCELLED'].includes(jobStatus.status) : false;
    const isSuccessful = jobStatus?.status === 'SUCCESS';
    const isFailed = jobStatus?.status === 'FAILED';
    const isPending = jobStatus?.status === 'PENDING';
    const isRunning = jobStatus?.status === 'RUNNING';

    return {
        jobStatus,
        isPolling,
        error,
        startPolling,
        stopPolling,
        reset,
        clearError,
        progress: jobStatus?.progress ?? 0,
        currentPhase: PHASE_LABELS[jobStatus?.current_phase ?? ''] ?? jobStatus?.current_phase ?? '',
        isComplete,
        isSuccessful,
        isFailed,
        isPending,
        isRunning,
        historicalReportId: jobStatus?.historical_report_id ?? null,
    };
};